
import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function HealthSupport() {
  const [activeCategory, setActiveCategory] = useState('health-consultation');

  const categories = [
    { id: 'health-consultation', name: '健康相談', icon: 'ri-heart-pulse-line' },
    { id: 'sarcopenia', name: 'サルコペニア予防', icon: 'ri-run-line' },
    { id: 'frailty', name: 'フレイル対策', icon: 'ri-shield-check-line' },
    { id: 'nutrition', name: '栄養指導', icon: 'ri-restaurant-line' },
    { id: 'exercise', name: '運動指導', icon: 'ri-bike-line' },
    { id: 'mental-health', name: 'メンタルヘルス', icon: 'ri-brain-line' }
  ];

  const healthConsultationContent = {
    title: 'セルフケアのサポート',
    description: '健康診断結果の詳細解説から日常の健康管理まで、専門薬剤師がサポートします',
    services: [
      {
        title: '健康診断結果解説',
        description: '血液検査、尿検査などの結果を分かりやすく説明し、改善点をアドバイス',
        icon: 'ri-file-chart-line',
        link: '/health-consultation/health-checkup-explanation'
      },
      {
        title: '生活習慣病予防',
        description: '糖尿病、高血圧、脂質異常症などの予防と管理方法をご提案',
        icon: 'ri-heart-line',
        link: '/health-consultation/lifestyle-disease-prevention'
      },
      {
        title: '薬物療法相談',
        description: '処方薬の効果、副作用、飲み合わせについて詳しく説明',
        icon: 'ri-capsule-line',
        link: '/health-consultation/medication-therapy-consultation'
      },
      {
        title: '健康管理計画',
        description: '個人の状況に合わせた健康管理プランを作成・フォロー',
        icon: 'ri-calendar-check-line',
        link: '/health-consultation/health-management-plan'
      }
    ]
  };

  const sarcopeniaContent = {
    title: 'サルコペニア予防プログラム',
    description: '筋肉量減少を防ぎ、健康的な老化をサポートする総合プログラム',
    services: [
      {
        title: '筋力測定・評価',
        description: '握力測定、歩行速度測定による筋力評価と改善計画の策定',
        icon: 'ri-hand-heart-line',
        link: '/sarcopenia/muscle-strength-assessment'
      },
      {
        title: '筋力トレーニング指導',
        description: '年齢・体力に応じた安全で効果的な筋力トレーニングメニュー',
        icon: 'ri-run-line',
        link: '/sarcopenia/strength-training-guidance'
      },
      {
        title: 'タンパク質摂取指導',
        description: '筋肉維持に必要なタンパク質の適切な摂取方法をアドバイス',
        icon: 'ri-restaurant-2-line',
        link: '/sarcopenia/protein-intake-guidance'
      },
      {
        title: '進捗モニタリング',
        description: '定期的な測定により効果を確認し、プログラムを調整',
        icon: 'ri-line-chart-line',
        link: '/sarcopenia/progress-monitoring'
      }
    ]
  };

  const frailtyContent = {
    title: 'フレイル対策プログラム',
    description: '身体的・精神的・社会的フレイルを総合的に予防・改善',
    services: [
      {
        title: 'フレイル評価',
        description: '包括的なフレイル評価により現在の状態を正確に把握',
        icon: 'ri-survey-line',
        link: '/frailty/frailty-assessment'
      },
      {
        title: '多面的介入',
        description: '運動、栄養、社会参加を組み合わせた総合的なアプローチ',
        icon: 'ri-team-line',
        link: '/frailty/multifaceted-intervention'
      },
      {
        title: '認知機能サポート',
        description: '認知機能の維持・向上を目的とした活動プログラム',
        icon: 'ri-brain-line',
        link: '/frailty/cognitive-function-support'
      },
      {
        title: '社会参加促進',
        description: '地域活動への参加を通じた社会的フレイルの予防',
        icon: 'ri-community-line',
        link: '/frailty/social-participation-promotion'
      }
    ]
  };

  const nutritionContent = {
    title: '栄養指導サポート',
    description: '個人の健康状態に合わせた栄養管理と食事指導',
    services: [
      {
        title: '栄養状態評価',
        description: '血液検査データと食事記録による詳細な栄養状態の評価',
        icon: 'ri-pie-chart-line',
        link: '/nutrition/nutritional-status-assessment'
      },
      {
        title: '食事プラン作成',
        description: '疾患や体質に応じた個別の食事プランを作成',
        icon: 'ri-file-list-3-line',
        link: '/nutrition/meal-plan-creation'
      },
      {
        title: 'サプリメント相談',
        description: '必要に応じたサプリメントの選択と適切な摂取方法',
        icon: 'ri-medicine-bottle-line',
        link: '/nutrition/supplement-consultation'
      },
      {
        title: '継続サポート',
        description: '定期的なフォローアップによる食習慣の改善支援',
        icon: 'ri-calendar-event-line',
        link: '/nutrition/continuous-support'
      }
    ]
  };

  const exerciseContent = {
    title: '運動指導プログラム',
    description: '安全で効果的な運動プログラムによる健康増進',
    services: [
      {
        title: '体力測定・評価',
        description: '現在の体力レベルを正確に測定し、適切な運動強度を設定',
        icon: 'ri-speed-line',
        link: '/exercise/fitness-assessment'
      },
      {
        title: '個別運動プログラム',
        description: '年齢、体力、疾患に応じたオーダーメイドの運動メニュー',
        icon: 'ri-user-settings-line',
        link: '/exercise/personalized-exercise-program'
      },
      {
        title: '運動指導・監督',
        description: '専門スタッフによる安全な運動指導と進捗管理',
        icon: 'ri-user-star-line',
        link: '/exercise/exercise-guidance-supervision'
      },
      {
        title: '生活活動改善',
        description: '日常生活での活動量増加のための具体的なアドバイス',
        icon: 'ri-walk-line',
        link: '/exercise/daily-activity-improvement'
      }
    ]
  };

  const mentalHealthContent = {
    title: 'メンタルヘルスサポート',
    description: '心の健康維持と精神的ウェルビーイングの向上',
    services: [
      {
        title: 'ストレス評価',
        description: '心理的ストレスの状態を評価し、適切な対処法を提案',
        icon: 'ri-emotion-line',
        link: '/mental-health/stress-assessment'
      },
      {
        title: 'リラクゼーション指導',
        description: '呼吸法、瞑想、マインドフルネスなどのリラクゼーション技法',
        icon: 'ri-leaf-line',
        link: '/mental-health/relaxation-guidance'
      },
      {
        title: '睡眠改善サポート',
        description: '良質な睡眠のための生活習慣改善と環境整備',
        icon: 'ri-moon-line',
        link: '/mental-health/sleep-improvement-support'
      },
      {
        title: '専門医連携',
        description: '必要に応じて精神科医やカウンセラーとの連携をサポート',
        icon: 'ri-links-line',
        link: '/mental-health/specialist-collaboration'
      }
    ]
  };

  const getContentByCategory = (categoryId: string) => {
    switch (categoryId) {
      case 'health-consultation': return healthConsultationContent;
      case 'sarcopenia': return sarcopeniaContent;
      case 'frailty': return frailtyContent;
      case 'nutrition': return nutritionContent;
      case 'exercise': return exerciseContent;
      case 'mental-health': return mentalHealthContent;
      default: return healthConsultationContent;
    }
  };

  const currentContent = getContentByCategory(activeCategory);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link to="/" className="flex items-center">
                <div className="w-10 h-10 mr-3 relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-lg transform rotate-12 shadow-lg"></div>
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-400 to-emerald-400 rounded-lg flex items-center justify-center">
                    <i className="ri-heart-pulse-fill text-white text-lg"></i>
                  </div>
                </div>
                <div className="text-xl font-bold text-teal-600" style={{ fontFamily: '"Pacifico", serif' }}>
                  みんなの薬局～Pharmacare～
                </div>
              </Link>
            </div>
            <nav className="hidden md:flex space-x-8">
              <Link to="/services" className="text-gray-700 hover:text-teal-600 font-medium whitespace-nowrap cursor-pointer">サービス</Link>
              <Link to="/health-support" className="text-teal-600 hover:text-teal-700 font-medium whitespace-nowrap cursor-pointer">健康サポート</Link>
              <Link to="/pharmacist-resources" className="text-gray-700 hover:text-teal-600 font-medium whitespace-nowrap cursor-pointer">薬剤師向け</Link>
              <Link to="/information" className="text-gray-700 hover:text-teal-600 font-medium whitespace-nowrap cursor-pointer">情報提供</Link>
            </nav>
            <button className="md:hidden p-2">
              <i className="ri-menu-line text-xl"></i>
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-green-50 to-teal-50 py-16">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/18fd32fac228263b60a9e769d6c5b549.jpg"
            alt="健康サポート"
            className="w-full h-full object-cover object-top opacity-20"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            健康<span className="text-teal-600">サポート</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            セルフケアから専門的な健康相談まで、あなたの健康維持をトータルサポート
          </p>
        </div>
      </section>

      {/* Category Navigation */}
      <section className="py-8 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-2 justify-center">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`flex items-center px-4 py-2 rounded-full font-medium transition-all whitespace-nowrap cursor-pointer ${
                  activeCategory === category.id
                    ? 'bg-teal-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-teal-50 hover:text-teal-600'
                }`}
              >
                <i className={`${category.icon} mr-2`}></i>
                {category.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">{currentContent.title}</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">{currentContent.description}</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {currentContent.services.map((service, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow p-8 border border-gray-100">
                <div className="w-12 h-12 bg-teal-50 text-teal-600 rounded-lg flex items-center justify-center mb-6">
                  <i className={`${service.icon} text-xl`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-6">{service.description}</p>
                <Link 
                  to={service.link}
                  className="inline-flex items-center text-teal-600 font-semibold hover:text-teal-700 cursor-pointer"
                >
                  詳細を見る
                  <i className="ri-arrow-right-line ml-2"></i>
                </Link>
              </div>
            ))}
          </div>

          {/* メンタルヘルス関連リンク */}
          {activeCategory === 'mental-health' && (
            <div className="mt-12 space-y-8">
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-8 border border-blue-100">
                <h3 className="text-xl font-bold text-gray-900 mb-4">おすすめのメンタルヘルスサポート</h3>
                <p className="text-gray-600 mb-6">専門的なメンタルヘルスケアをお探しの方におすすめです</p>
                <div className="flex justify-center">
                  <a href="https://px.a8.net/svt/ejp?a8mat=45FY5Y+8KEK4Y+5OI8+5ZEMP" rel="nofollow" className="cursor-pointer">
                    <img border="0" width="300" height="250" alt="メンタルヘルスサポート" src="https://www23.a8.net/svt/bgt?aid=251006902518&wid=001&eno=01&mid=s00000026504001005000&mc=1" className="rounded-lg shadow-md hover:shadow-lg transition-shadow" />
                  </a>
                  <img border="0" width="1" height="1" src="https://www15.a8.net/0.gif?a8mat=45FY5Y+8KEK4Y+5OI8+5ZEMP" alt="" />
                </div>
              </div>
              
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-8 border border-purple-100 text-center">
                <h3 className="text-xl font-bold text-gray-900 mb-4">オンラインカウンセリングサービス</h3>
                <p className="text-gray-600 mb-6">いつでもどこでも専門カウンセラーに相談できるオンラインサービス</p>
                <div className="inline-block bg-white rounded-lg p-6 shadow-md hover:shadow-lg transition-shadow">
                  <a href="https://px.a8.net/svt/ejp?a8mat=45FY5Y+8KEK4Y+5OI8+5YZ76" rel="nofollow" className="text-purple-600 hover:text-purple-700 font-semibold text-lg cursor-pointer">
                    オンラインカウンセリング「Kimochi」
                  </a>
                  <img border="0" width="1" height="1" src="https://www18.a8.net/0.gif?a8mat=45FY5Y+8KEK4Y+5OI8+5YZ76" alt="" />
                  <div className="mt-3 text-sm text-gray-500">
                    <i className="ri-computer-line mr-2"></i>
                    オンラインで気軽にカウンセリング
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 mr-2 relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-400 to-emerald-400 rounded-lg transform rotate-12"></div>
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-300 to-emerald-300 rounded-lg flex items-center justify-center">
                    <i className="ri-heart-pulse-fill text-white text-sm"></i>
                  </div>
                </div>
                <div className="text-xl font-bold text-teal-400" style={{ fontFamily: '"Pacifico", serif' }}>
                  みんなの薬局～Pharmacare～
                </div>
              </div>
              <p className="text-gray-400">
                患者さんと薬剤師をつなぐ総合医療情報プラットフォーム
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">健康サポート</h3>
              <ul className="space-y-2 text-gray-400">
                <li><button onClick={() => setActiveCategory('health-consultation')} className="hover:text-white cursor-pointer">健康相談</button></li>
                <li><button onClick={() => setActiveCategory('sarcopenia')} className="hover:text-white cursor-pointer">サルコペニア予防</button></li>
                <li><button onClick={() => setActiveCategory('frailty')} className="hover:text-white cursor-pointer">フレイル対策</button></li>
                <li><button onClick={() => setActiveCategory('nutrition')} className="hover:text-white cursor-pointer">栄養指導</button></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">薬剤師向け</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/pharmacist-resources" className="hover:text-white cursor-pointer">専門リソース</Link></li>
                <li><Link to="/sterile-preparation" className="hover:text-white cursor-pointer">無菌調剤</Link></li>
                <li><Link to="/home-care" className="hover:text-white cursor-pointer">在宅医療</Link></li>
                <li><Link to="/dx" className="hover:text-white cursor-pointer">薬局DX</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">情報・リンク</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/information" className="hover:text-white cursor-pointer">公的機関リンク</Link></li>
                <li><Link to="/guidelines" className="hover:text-white cursor-pointer">疾患ガイドライン</Link></li>
                <li><Link to="/career" className="hover:text-white cursor-pointer">転職情報</Link></li>
                <li><Link to="/entertainment" className="hover:text-white cursor-pointer">エンタメ</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 みんなの薬局～Pharmacare～. All rights reserved. | <a href="https://readdy.ai/?origin=logo" className="hover:text-white cursor-pointer">Website Builder</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}
